import api from "../../../libs/axios"
import { PaginationType } from "../../../utils/pagination.utils"

/**
 * Shared plumbing for the Material Management screens.
 *
 * Every list in this module is project-scoped on the server: the caller sees
 * their own projects and nothing else, and a project filter outside their
 * scope simply returns no rows. Nothing here needs to know that - the point is
 * that the client must never try to widen it, so there is no "all projects"
 * escape hatch anywhere below.
 */

export type HolderType = "warehouse" | "staff" | "site"

export const HOLDER_LABELS: Record<HolderType, string> = {
  warehouse: "Warehouse",
  staff: "Staff",
  site: "Site",
}

export interface FilterOption {
  id: number
  code?: string | null
  name: string
}

/** Who supplies a request / arrangement: a Material Supplier, or a vendor from the Vendor master. */
export type SupplierKind = "supplier" | "vendor"

/** The supplier's name on a request or arrangement row, whichever master it came from. */
export const supplierDisplayName = (row: any, fallback = "-"): string =>
  row?.supplier?.supplier_name || row?.supplier_vendor?.vendor_full_name || row?.supplier_name || fallback

/** The API returns pagination in a different shape from the Pagination widget. */
export function toPagination(pg: any): PaginationType | null {
  if (!pg) return null
  return {
    offset: (Number(pg.current_page || 1) - 1) * Number(pg.per_page || 10),
    limit: Number(pg.per_page ?? 10),
    page: Number(pg.current_page ?? 1),
    total_pages: Number(pg.total_pages ?? 1),
    total_count: Number(pg.total ?? 0),
    has_next_page: !!pg.has_next_page,
    has_prev_page: !!pg.has_prev_page,
    next_page: pg.next_page ?? null,
    prev_page: pg.prev_page ?? null,
  }
}

export interface PagedResult<T = any> {
  rows: T[]
  pagination: PaginationType | null
}

export async function fetchPaged<T = any>(url: string, params: Record<string, any>): Promise<PagedResult<T>> {
  const res = await api.get(url, { params: stripEmpty(params) })
  const body = res.data?.data ?? {}
  return { rows: body.data || [], pagination: toPagination(body.pagination) }
}

/** Drop undefined/empty params so the server sees "no filter" rather than "". */
export function stripEmpty(params: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return
    out[key] = value
  })
  return out
}

// ── Scoped filter dropdowns ──────────────────────────────────────────────
//
// These come off /materials/filters/*, NOT off the master tables. The server
// derives them from material that is actually in the caller's projects, so an
// option can never be offered that the data could not return. Fetching
// warehouses from /materials/warehouses instead would quietly reintroduce
// exactly that bug.

export const materialFilters = {
  projects: (search?: string) => fetchOptions("/materials/filters/projects", "projects", { search }),
  warehouses: (projectId?: string, search?: string) =>
    fetchOptions("/materials/filters/warehouses", "warehouses", { project_id: projectId, search }),
  staff: (projectId?: string, search?: string) =>
    fetchOptions("/materials/filters/staff", "staff", { project_id: projectId, search }),
  sites: (projectId?: string, search?: string) =>
    fetchOptions("/materials/filters/sites", "sites", { project_id: projectId, search }),
  items: (projectId?: string, search?: string) =>
    fetchOptions("/materials/filters/items", "items", { project_id: projectId, search }),
}

async function fetchOptions(url: string, key: string, params: Record<string, any>): Promise<FilterOption[]> {
  const res = await api.get(url, { params: stripEmpty(params) })
  const rows = res.data?.data?.[key] || []
  return rows.map((row: any) => ({
    id: Number(row.id),
    code: row.code ?? row.project_short_name ?? row.item_code ?? null,
    name: row.name ?? row.project_name ?? row.item_name ?? `#${row.id}`,
  }))
}

/**
 * The master pickers used when BUILDING a document, as opposed to filtering
 * one. These do come off the masters: a new request may well be the first
 * time an item or a warehouse is used on a project, so restricting the picker
 * to what already has movement would make it impossible to start.
 */
export const materialMasters = {
  items: async (search?: string): Promise<FilterOption[]> => {
    const res = await api.get("/materials/items", { params: stripEmpty({ search, limit: 50, is_active: true }) })
    return (res.data?.data?.items || []).map((row: any) => ({
      id: Number(row.id),
      code: row.item_code,
      name: row.item_name,
    }))
  },
  warehouses: async (search?: string): Promise<FilterOption[]> => {
    const res = await api.get("/materials/warehouses", { params: stripEmpty({ search, limit: 50, is_active: true }) })
    return (res.data?.data?.items || []).map((row: any) => ({
      id: Number(row.id),
      code: row.warehouse_code,
      name: row.warehouse_name,
    }))
  },
  suppliers: async (search?: string): Promise<FilterOption[]> => {
    const res = await api.get("/materials/suppliers", { params: stripEmpty({ search, limit: 50, is_active: true }) })
    return (res.data?.data?.items || []).map((row: any) => ({
      id: Number(row.id),
      code: row.supplier_code,
      name: row.supplier_name,
    }))
  },
  /**
   * Vendors from the Vendor master, which can stand in as a supplier on a
   * request or an arrangement. Organisation-wide (all active vendors).
   */
  vendors: async (search?: string): Promise<FilterOption[]> => {
    const res = await api.get("/vendor/list", { params: stripEmpty({ search, limit: 50, status: "active" }) })
    return (res.data?.data?.vendors || []).map((row: any) => ({
      id: Number(row.id),
      code: row.vendor_code || row.vendor_short_name,
      name: row.vendor_full_name,
    }))
  },
  transporters: async (search?: string): Promise<FilterOption[]> => {
    const res = await api.get("/materials/transporters", { params: stripEmpty({ search, limit: 50, is_active: true }) })
    return (res.data?.data?.items || []).map((row: any) => ({
      id: Number(row.id),
      code: row.transporter_code,
      name: row.transporter_name,
    }))
  },
}

// ── Formatting ────────────────────────────────────────────────────────────

export const num = (v: any) => Number(v || 0)

/** Quantities are DECIMAL(16,3); trailing zeros on a whole number are noise. */
export const qty = (v: any) => {
  const n = Number(v || 0)
  return Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/0+$/, "").replace(/\.$/, "")
}

export const money = (v: any) =>
  Number(v || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 })

export const dateText = (v: any) => (v ? new Date(v).toLocaleDateString("en-IN") : "-")

export const dateTimeText = (v: any) => (v ? new Date(v).toLocaleString("en-IN") : "-")

export const fullName = (u: any) =>
  u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username || "-" : "-"

/** The server's error message is the useful one - "Insufficient stock for item #12: …". */
export const apiError = (e: any, fallback: string) => e?.response?.data?.message || fallback

export const STATUS_BADGE: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  requested: "bg-yellow-100 text-yellow-800",
  submitted: "bg-yellow-100 text-yellow-800",
  pending_approval: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  approved_l1: "bg-teal-100 text-teal-800",
  arranged: "bg-indigo-100 text-indigo-800",
  proceed_for_payment: "bg-purple-100 text-purple-800",
  paid: "bg-emerald-100 text-emerald-800",
  issued: "bg-blue-100 text-blue-800",
  received: "bg-green-100 text-green-800",
  delivered: "bg-green-100 text-green-800",
  completed: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-red-100 text-red-800",
}

export const statusLabel = (s: string) =>
  String(s || "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
